import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from "react";
import {
	Icon,
	Spinner,
	TreeAccordion,
	TreeView,
	TreeViewItem,
	TreeViewHandle,
	TreeViewActionsAndContext
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { ViewsLoadingState } from "./SharePointStore";
import {
	DocumentsHierarchyNode} from "client_legacy/Documents/SharePoint/DocumentsViewsPane/types";
import {
	DocumentsViewContainerViewModel,
	DocumentsViewContainerViewModelExtendedInfo,
	getSiteContent,
	LoadedSiteContentViewModel
} from "client/Api/Documents/SharePoint/sharepointDocuments";
import {
	DocumentsViewModel,
	DocumentsViewType,
	LoadingState
} from "client/Api/Documents/SharePoint/common";
import { reloadCurrentPage } from "client_legacy/ndaw/navigator";
import { NewSubsiteDialog } from "./Dialogs/NewSubsiteDialog";
import {
	Action,
	ActionAndOthers,
	ActionKind
} from "client_legacy/ndaw/actions";
import { SitePermissionsDialog } from "./SitePermissions/SitePermissionsDialog";
import { never } from "client_legacy/ndaw/errors";
import styles from "./SharePointSideBarMenu.module.less";
import { ExtendedInfoRequester } from "client_legacy/Documents/SharePoint/extendedInfoRequester";
import { useToggleExpansion } from "client/Components/TreeAccordion/useToggleExpansion";
import { useUnableToConnectToSharePointDialog } from "./Dialogs/UnableToConnectToSharePointDialog";
import {
	AlertManager,
	useAlerts,
	useIsMounted,
	useStableImperativeCallback
} from "client/Hooks";
import { EMPTY_ARRAY } from "client/Misc/utils";

type MenuContext = {
	siteUrl: string;
	canCreateSubsite: boolean;
	canManagePermissions: boolean;
};

enum EditSiteActionType {
	NewSubSite = 1,
	EditPermissions = 2
}

type EditSiteContextMenuState = {
	siteUrl: string;
	type: EditSiteActionType;
};

export type SharePointSideBarHandle = {
	expandParentNodes(id: string): void;
};

export type SharePointSideBarMenuProps = {
	viewsLoadingState: ViewsLoadingState;
	onSelectView(view: DocumentsViewModel): void;
	extendedInfoRequester?: ExtendedInfoRequester;
	selectedView: DocumentsViewModel | undefined;
	onSiteContentLoaded: (id: string, content: LoadedSiteContentViewModel) => void;
} & (
	{
		doNotRenderActions?: false;
		onActionInvoked(): void;
	}
	|
	{
		doNotRenderActions: true;
		onActionInvoked?: undefined;
	}
);

export const isSharePointContainerItem = (item: DocumentsHierarchyNode): item is DocumentsViewContainerViewModel => {
	return item.hasOwnProperty("content");
};

const SHARE_POINT_ITEM_ID: string = "share-point";

export const SharePointSideBarMenu = forwardRef(
	(
		props: SharePointSideBarMenuProps,
		ref: Ref<SharePointSideBarHandle>
	): ReactElement => {
		const {
			viewsLoadingState,
			onSelectView,
			extendedInfoRequester,
			selectedView,
			onActionInvoked,
			doNotRenderActions = false,
			onSiteContentLoaded: onSiteContentLoadedFromProps
		} = props;

		const [editSiteContextMenuState, setEditSiteContextMenuState] = useState<EditSiteContextMenuState | null>(null);
		const [siteContentBeingLoaded, setSiteContentBeingLoaded] = useState<ReadonlySet<string>>(new Set<string>());

		const [UnableToConnectToSharePointDialog, openUnableToConnectToSharePointDialog] = useUnableToConnectToSharePointDialog();

		const treeRef: RefObject<TreeViewHandle<DocumentsHierarchyNode> | null> = useRef<TreeViewHandle<DocumentsHierarchyNode>>(null);

		const alertManager: AlertManager = useAlerts();
		const { isExpanded, toggleExpansion } = useToggleExpansion([SHARE_POINT_ITEM_ID]);
		const isMounted: () => boolean = useIsMounted();

		const headerClickHandler = async (): Promise<void> => {
			const result: TryResult<void> = await openUnableToConnectToSharePointDialog({});

			if (result.wasSuccessful) {
				reloadCurrentPage();
			}
		};

		const actions = useMemo(
			(): ReadonlyArray<Action<MenuContext>> => [
				{
					kind: ActionKind.Regular,
					name: "NewSubSite",
					title: localize("_NewSubsite"),
					icon: "plus",
					canExecute: (c) => c.canCreateSubsite,
					execute: (c) => {
						onActionInvoked?.();

						setEditSiteContextMenuState({
							siteUrl: c.siteUrl,
							type: EditSiteActionType.NewSubSite
						});
					}
				},
				{
					kind: ActionKind.Regular,
					name: "Permissions",
					title: localize("_Permissions"),
					icon: "key",
					canExecute: (c) => c.canManagePermissions,
					execute: (c) => {
						onActionInvoked?.();

						setEditSiteContextMenuState({
							siteUrl: c.siteUrl,
							type: EditSiteActionType.EditPermissions
						});
					}
				}
			],
			[onActionInvoked]
		);

		const preparedStructure = useMemo(
			(): readonly DocumentsHierarchyNode[] => {
				if (viewsLoadingState.kind !== "success") {
					return EMPTY_ARRAY;
				}

				return prepareSidebarStructure(viewsLoadingState.items);
			},
			[viewsLoadingState]
		);

		const itemsData = useMemo(
			(): Map<string, DocumentsHierarchyNode> => {
				return buildItemsData(preparedStructure);
			},
			[preparedStructure]
		);

		const rootItems = useMemo(
			(): readonly TreeViewItem<DocumentsHierarchyNode>[] => {
				return preparedStructure
					.map(i => convertToTreeViewItem(
						i,
						doNotRenderActions
							? undefined
							: actions,
						siteContentBeingLoaded
					));
			},
			[
				actions,
				doNotRenderActions,
				preparedStructure,
				siteContentBeingLoaded
			]
		);

		const selectHandler = useCallback(
			(item: DocumentsHierarchyNode): void => {

				if (!isSharePointContainerItem(item)) {
					onSelectView(item);
				}
			},
			[
				onSelectView
			]
		);

		const closeDialogHandler = useCallback(
			(): void => setEditSiteContextMenuState(null),
			[]
		);

		const renderDialog = (): ReactElement | null => {
			if (!editSiteContextMenuState) {
				return null;
			}

			const {
				type,
				siteUrl
			} = editSiteContextMenuState;

			switch (type) {
				case EditSiteActionType.NewSubSite:
					return <NewSubsiteDialog siteUrl={siteUrl} onClose={closeDialogHandler} />;
				case EditSiteActionType.EditPermissions:
					return <SitePermissionsDialog siteUrl={siteUrl} onClose={closeDialogHandler} />;
				default:
					never(type);
			}
		};

		const onSiteContentLoaded: (id: string, content: LoadedSiteContentViewModel) => void = useStableImperativeCallback(onSiteContentLoadedFromProps);

		const expandHandler = useCallback(
			async (item: DocumentsHierarchyNode): Promise<void> => {

				if (!isSharePointContainerItem(item)) {
					throw Error("Expanded item is not a container");
				}

				let { content } = item;
				if (!content.isLoaded) {
					setSiteContentBeingLoaded(
						(x): ReadonlySet<string> => new Set(x)
							.add(item.id)
					);

					try {
						content = await getSiteContent(item.url);
					} catch (e) {
						console.error(e);

						content = {
							isLoaded: true,
							hasError: true,
							subContainers: [],
							views: []
						};
					} finally {
						if (!isMounted()) {
							return;
						}

						setSiteContentBeingLoaded(
							(x): ReadonlySet<string> => {
								const result = new Set(x);
								result.delete(item.id);
								return result;
							}
						);
					}

					if (content.isLoaded && content.hasError) {
						alertManager.showError(localize("_FailedToLoadDocumentLibraries"));
					}

					onSiteContentLoaded(item.id, content);
				}

				const siteUrls: readonly string[] = getSiteUrlsToLoadExtendedData({ ...item, content });

				extendedInfoRequester?.queueFor(siteUrls);
			},
			[
				alertManager,
				extendedInfoRequester,
				isMounted,
				onSiteContentLoaded
			]
		);

		const expandParentNodes = useCallback(
			(id: string): void => {
				if (!treeRef.current) {
					throw Error("Tree's ref is not set");
				}

				if (itemsData.size === 0) {
					throw Error("There is no data for the items");
				}

				const parentNodes: readonly DocumentsHierarchyNode[] = getParentNodes(id, itemsData);

				if (parentNodes.length === 0) {
					return;
				}

				treeRef.current.expandNodes(parentNodes);

				const allSiteUrls: string[] = [];

				parentNodes.forEach((parentNode: DocumentsHierarchyNode) => {
					if (!isSharePointContainerItem(parentNode)) {
						throw Error(`The parent node with id ${parentNode} is not a container`);
					}

					if (parentNode.extendedInfo.state === LoadingState.NotLoaded) {
						allSiteUrls.push(parentNode.url);
					}

					const siteUrls: readonly string[] = getSiteUrlsToLoadExtendedData(parentNode);
					allSiteUrls.push(...siteUrls);
				});

				extendedInfoRequester?.queueFor(allSiteUrls);
			},
			[
				extendedInfoRequester,
				itemsData
			]
		);

		useImperativeHandle(
			ref,
			(): SharePointSideBarHandle => ({
				expandParentNodes
			}),
			[expandParentNodes]
		);

		const toggleExpansionHandler = useCallback(
			(): void => toggleExpansion(SHARE_POINT_ITEM_ID),
			[toggleExpansion]
		);

		return (
			<>
				<TreeAccordion trackingId="share-point-sidebar">
					<TreeAccordion.Node>
						<TreeAccordion.Item
							{...(
								viewsLoadingState.kind === "success"
									? {
										expansionToggle: "visible",
										isExpanded: isExpanded(SHARE_POINT_ITEM_ID),
										onToggleExpansion: toggleExpansionHandler
									}
									: {
										expansionToggle: "hidden"
									}
							)}
							icon="cloud"
							onClick={viewsLoadingState.kind === "error"
								? headerClickHandler
								: toggleExpansionHandler
							}
							trackingId="share-point-sidebar__header"
						>
							<NodeContent
								text={localize("_SharePointStoreTitle")}
								status={viewsLoadingState.kind}
							/>
						</TreeAccordion.Item>
						{viewsLoadingState.kind === "success" && (
							<TreeView
								ref={treeRef}
								value={selectedView}
								onChange={selectHandler}
								items={rootItems}
								compareBy="id"
								onToggleExpand={expandHandler}
								indentionLevelOffset={1}
								isCollapsed={!isExpanded(SHARE_POINT_ITEM_ID)}
								trackingId="share-point-tree-view"
							/>
						)}
					</TreeAccordion.Node>
				</TreeAccordion>
				{renderDialog()}
				{UnableToConnectToSharePointDialog}
			</>
		);
	}
);
SharePointSideBarMenu.displayName = "SharePointSideBarMenu";

export type NodeContentProps = {
	text: string;
	status?: "loading" | "error" | "success";
};

export const NodeContent = (props: NodeContentProps): ReactElement => {
	const {
		text,
		status
	} = props;

	return (
		<div className={styles.content}>
			<div className={styles.text}>{text}</div>
			{status === "loading" && <Spinner isInline />}
			{status === "error"
				&& (
					<Icon
						value={{
							kind: "font",
							name: "triangle-exclamation",
							color: "danger"
						}}
					/>
				)
			}
		</div>
	);
};
NodeContent.displayName = "NodeContent";

function shouldSiteActionMenuBeVisible(info: DocumentsViewContainerViewModelExtendedInfo): boolean {
	return info.canCreateSubsite || info.canManagePermissions;
}

export function buildItemsData(
	items: readonly DocumentsHierarchyNode[],
): Map<string, DocumentsHierarchyNode> {
	const newMap: Map<string, DocumentsHierarchyNode> = new Map();

	for (const item of flattenItems(items)) {
		newMap.set(
			item.id,
			item
		);
	}

	return newMap;
};

export function flattenItems(
	items: readonly DocumentsHierarchyNode[]
): readonly DocumentsHierarchyNode[] {
	const result: DocumentsHierarchyNode[] = [];

	for (const item of items) {
		result.push(item);

		if (!isSharePointContainerItem(item)) {
			continue;
		}

		const { content } = item;
		if (!content.isLoaded) {
			continue;
		}

		result.push(
			...flattenItems(
				[
					...content.subContainers,
					...content.views
				]
			)
		);
	}

	return result;
}

export function getItemData(
	id: string,
	itemsData: Map<string, DocumentsHierarchyNode>
): DocumentsHierarchyNode {
	const item: DocumentsHierarchyNode | undefined = itemsData.get(id);

	if(!item) {
		throw Error(`There is no data for item with id ${id}`);
	}

	return item;
}

export function getParentNodes(
	id: string,
	itemsData: Map<string, DocumentsHierarchyNode>,
): readonly DocumentsHierarchyNode[] {

	const parent: DocumentsHierarchyNode | undefined = [...itemsData.values()]
		.find(
			(x) => {
				if (!isSharePointContainerItem(x)) {
					return false;
				}

				const { content } = x;
				if (!content.isLoaded) {
					return false;
				}

				return [
					...content.subContainers,
					...content.views
				]
					.some(item => item.id === id);
			}
		);

	if (!parent) {
		return [];
	}

	return [
		parent,
		...getParentNodes(
			parent.id,
			itemsData
		)
	];
}

function getSiteUrlsToLoadExtendedData(
	item: DocumentsViewContainerViewModel
): readonly string[] {
	const { content } = item;
	if (!content.isLoaded) {
		return EMPTY_ARRAY;
	}

	const siteUrls: string[] = [];
	for (const container of content.subContainers) {
		if (container.extendedInfo.state === LoadingState.NotLoaded) {
			siteUrls.push(container.url);
		}
	}

	return siteUrls;
}

function convertToTreeViewItem(
	node: DocumentsHierarchyNode,
	actions: readonly ActionAndOthers<MenuContext>[] | undefined,
	siteContentBeingLoaded: ReadonlySet<string>
): TreeViewItem<DocumentsHierarchyNode> {
	const getItemActions = (): TreeViewActionsAndContext<MenuContext> | undefined => {
		if (
			!actions
				||!isSharePointContainerItem(node)
				|| node.isPersonal
				|| node.extendedInfo.state !== LoadingState.Loaded
		) {
			return undefined;
		}

		const {
			canCreateSubsite,
			canManagePermissions
		} = node.extendedInfo.info;

		const context: MenuContext = {
			siteUrl: node.url,
			canCreateSubsite,
			canManagePermissions
		};

		return shouldSiteActionMenuBeVisible(context)
			? (
				{
					actions,
					context
				}
			)
			: undefined;
	};

	let title: string = node.title;
	let status: NodeContentProps["status"];
	let children: TreeViewItem<DocumentsHierarchyNode>["children"] = undefined;

	if (isSharePointContainerItem(node)) {
		const { id, content } = node;
		if (content.isLoaded) {
			children = [
				...content.subContainers,
				...content.views
			]
				.map(n => convertToTreeViewItem(n, actions, siteContentBeingLoaded));

			if (content.hasError) {
				status = "error";
				title = localize("_FailedToLoadDocumentLibraries");
			}
		} else {
			children = "lazy";

			if (siteContentBeingLoaded.has(id)) {
				status = "loading";
			}
		}
	}

	return {
		value: node,
		content: () => (
			<NodeContent
				text={node.title}
				status={status}
			/>
		),
		title,
		children,
		actions: getItemActions()
	};
}

export function prepareSidebarStructure(
	structure: readonly DocumentsViewContainerViewModel[]
): readonly DocumentsHierarchyNode[] {
	const result: DocumentsViewContainerViewModel[] = [];
	let recentlyModifiedItem: DocumentsViewModel | undefined;

	for (const item of structure) {
		if (!item.content.isLoaded || recentlyModifiedItem) {
			result.push(item);
			continue;
		}

		recentlyModifiedItem = item
			.content
			.views
			.find(
				x => x.type === DocumentsViewType.RecentlyModified
			);

		if (!recentlyModifiedItem) {
			result.push(item);
			continue;
		}

		result.push({
			...item,
			content: {
				...item.content,
				views: item
					.content
					.views
					.filter(
						x => x.type !== DocumentsViewType.RecentlyModified
					)
			}
		});
	}

	return recentlyModifiedItem
		? [
			recentlyModifiedItem,
			...result
		]
		: result;
}
